import { getUniqueId } from '../commonLib';
import { pureFunctionalMap, pureFunctionalMerge } from '../pureFunctionalLib';
import { ScanTemplateGroup, ScanTemplateRoot, ScanTemplateTask } from '../ScanTemplateRoot';
import { addErrorToIssues, ScanTemplateValidationDetails } from '../validateTemplate';
import { getStandardLineItem } from './standardLineItemGenerator';
import { isOtherTaskTemplate } from './StandardScanGetters';

const generateStandardOtherTask = () => ({
  id: getUniqueId(),
  name: 'Non-Standard Task',
  category: 'non-standard',
  description: "Add custom, non-standard task. Make sure there isn't a standard task before using this one.",
  hidden: true, // non-standard items will start out hidden, but the UX allows them to be added as needed
  subtype: 'otherTask',
  values: {
    taskDescription: { type: 'text' },
  },
  optionTemplates: [
    {
      id: getUniqueId(),
      name: 'Custom Work',
      // scopeTimeSku: non-standard line item, so let the user pick any catalog product at the scope stage
      lineItemTemplates: [getStandardLineItem({ costType: 'materialAndLabor', skuMode: 'scopeTimeSku' }, { name: 'Parts and Labor' })],
    },
  ],
});

const ensureTaskIsAttachedToAllGroups = (scanTemplateRoot: ScanTemplateRoot, otherTaskIds: Set<string>): ScanTemplateGroup[] | undefined => {
  if (!scanTemplateRoot.groupTemplates) return scanTemplateRoot.groupTemplates;
  const exampleOtherTaskId = otherTaskIds.values().next().value!;
  if (!exampleOtherTaskId) throw new Error('No other task ID found');
  return pureFunctionalMap(scanTemplateRoot.groupTemplates, group => {
    if (
      Array.isArray(group.taskTemplateIds)
      && group.taskTemplateIds.some(taskTemplateId => otherTaskIds.has(taskTemplateId))
    ) {
      return group;
    }
    return { ...group, taskTemplateIds: group.taskTemplateIds ? [...group.taskTemplateIds, exampleOtherTaskId] : [exampleOtherTaskId] };
  });
};

/**
 * All standardized templates should have exactly one other task and it should
 * be attached to all groups. If no other task exists, a standard version of the
 * other task will be added and it will be attached to all groups.
 *
 * Post-condition: Upon return, there will be standard "other task" and it will
 * be attached to all groups. If there were multiple other tasks, the first one
 * will be used and the issue will be added to the issues array. If there was no
 * other task, a new one will be created.
 *
 * Pure-Functional && Idempotent:
 * - the function will return the exact object passed in if it already meets the
 *   post-condition
 * - the 2nd of two successive calls is guaranteed to return the input untouched
 */
export const repairStandardizedTemplateOtherTasks = (scanTemplateRoot: ScanTemplateRoot, issues: ScanTemplateValidationDetails[] = []): ScanTemplateRoot => {
  const otherTasks = Object.values(scanTemplateRoot.taskTemplatesById ?? {}).filter(template => isOtherTaskTemplate(template));
  let newOtherTask: ScanTemplateTask | undefined = undefined;
  if (otherTasks.length === 0) {
    newOtherTask = generateStandardOtherTask();
  }

  const otherTaskIdsSet = new Set(newOtherTask ? [newOtherTask.id] : otherTasks.map(t => t.id));

  return pureFunctionalMerge(scanTemplateRoot, {
    groupTemplates: ensureTaskIsAttachedToAllGroups(scanTemplateRoot, otherTaskIdsSet),
    taskTemplatesById: newOtherTask
      ? {
        ...scanTemplateRoot.taskTemplatesById,
        [newOtherTask.id]: newOtherTask,
      }
      : scanTemplateRoot.taskTemplatesById,
  });
};

export const validateStandardizedTemplateOtherTasks = (scanTemplateRoot: ScanTemplateRoot, issues: ScanTemplateValidationDetails[]): void => {
  const otherTasks = Object.values(scanTemplateRoot.taskTemplatesById ?? {}).filter(template => isOtherTaskTemplate(template));
  const otherTaskIdsSet = new Set(otherTasks.map(t => t.id));
  if (otherTasks.length === 0) {
    addErrorToIssues(issues, 'There are no tasks of subtype "otherTask" in the template. There should be exactly one.', {});
    return; // ALL groups will be missing the other task, so we can return early rather than spamming the issues array
  }
  // find any groups w/o other tasks
  if (!scanTemplateRoot.groupTemplates) return;
  scanTemplateRoot.groupTemplates.forEach(group => {
    if (group.taskTemplateIds) {
      const groupHasOtherTask = group.taskTemplateIds.some(taskTemplateId => otherTaskIdsSet.has(taskTemplateId));
      if (!groupHasOtherTask) {
        addErrorToIssues(
          issues,
          `${group.name} Group: has no tasks with subtype "otherTask". As least one required to support adding custom tasks in the field.`,
          { groupId: group.id },
        );
      }
    }
  });
};
